import {
  GetAllPlatformCatalogVersionsParams,
  getGetPlatformCatalogByIdQueryKey,
  getGetPlatformCatalogsQueryKey,
  getPlatformCatalogByIdResponse,
  PlatformCatalogDTO,
  PublicationStatus,
  useGetPlatformCatalogByIdSuspense,
  useUpdatePlatformCatalog,
} from "@bvhi/backend-api/apis";
import { Card, Stack } from "@bvhi/ui-base";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import { pick, useParams } from "@tanstack/react-router";
import { Suspense, useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AnyObjectSchema, object, string } from "yup";
import { BVHINavigationBackButton } from "../../../../../components/bvhi-navigation-back-button";
import { BVHINavigationCreateEntityButton } from "../../../../../components/bvhi-navigation-create-entity-button";
import { FormBlockerDialog } from "../../../../../components/form/form-blocker-dialog";
import { NavigationBar } from "../../../../../components/navigation-bar";
import { useApiErrorContext } from "../../../../../context/use-api-error-context";
import { useAuthContext } from "../../../../../context/use-auth-context";
import { pickIfSet } from "../../../../../helpers/object";
import { withErrorThrowing } from "../../../../../hoc/with-error-throwing";
import { useBVHIDataGridFilter } from "../../../../../hooks/use-bvhi-datagrid-Filter";
import { useToast } from "../../../../../hooks/use-toast";
import { Route } from "../../../../../routes/_main/app/catalogs.catalog.$catalogId.index";
import { toNarrowedTypeOrNull } from "../../../../../utils/ts";
import { CatalogRulesCard } from "./components/catalog-rules-card";
import { CatalogUpdateCard } from "./components/catalog-update-card";
import { CatalogVersionsDataGrid } from "./components/datagrid/catalog-versions-datagrid";
import { useCatalogVersionsSortParams } from "./hooks/use-catalog-versions-sort-params";

export interface UpdateCatalogFormValues {
  name: string;
  notes: string;
  platformCatalogStatus: PublicationStatus;
}

export const CatalogsUpdateView = () => {
  const { catalogId } = useParams({ strict: false });
  if (!catalogId) throw new Error("catalogId not found in path");

  const searchParams = Route.useSearch();
  const { currentUser } = useAuthContext();
  const { mutate: updatePlatformCatalog } = useUpdatePlatformCatalog();
  const { getSortConfig } = useCatalogVersionsSortParams();
  const { handleDataGridChange, requestParams } = useBVHIDataGridFilter<GetAllPlatformCatalogVersionsParams>(
    {},
    Route.fullPath,
    getSortConfig,
    searchParams
  );
  const { t } = useTranslation(["validation", "common", "catalogs"]);
  const queryClient = useQueryClient();
  const { pushCreateErrorToast } = useToast();
  const { setErrors } = useApiErrorContext();

  const { data: getPlatformCatalogByIdResponse, dataUpdatedAt: getPlatformCatalogByIdUpdatedAt } =
    withErrorThrowing<getPlatformCatalogByIdResponse>(useGetPlatformCatalogByIdSuspense(currentUser?.organisation?.id ?? "", catalogId));

  const updateCatalogSchema: AnyObjectSchema = object({
    name: string().min(1, t("validation:string.min.1")).required(t("validation:required")),
    platformCatalogStatus: string().oneOf(Object.values(PublicationStatus)).required(t("validation:required")),
    notes: string()
      .nullable()
      .max(255, t("validation:string.max.number", { value: 255 }))
      .optional(),
  });

  const {
    formState: { isDirty, isValid, isSubmitSuccessful, ...otherFormState },
    reset,
    handleSubmit,
    ...otherMethods
  } = useForm<UpdateCatalogFormValues>({
    resolver: yupResolver(updateCatalogSchema),
    mode: "all",
    shouldUnregister: false,
  });

  const catalog = toNarrowedTypeOrNull<PlatformCatalogDTO>(getPlatformCatalogByIdResponse?.data, "id");

  useEffect(() => {
    if (!catalog) return;
    if (isDirty) return;
    const selectedData = pick(catalog, ["name", "notes", "platformCatalogStatus"]);
    reset(
      {
        ...selectedData,
      },
      { keepDirtyValues: true }
    );
  }, [catalog, getPlatformCatalogByIdUpdatedAt, isDirty, reset]);

  const onSubmit: SubmitHandler<UpdateCatalogFormValues> = (data) => {
    if (!catalog) return;
    const values = pickIfSet(data, ["name", "notes", "platformCatalogStatus"]);

    updatePlatformCatalog(
      {
        supplierId: currentUser?.organisation?.id ?? "",
        platformCatalogId: catalogId,
        data: {
          id: catalogId,
          platformCatalogAccessMatchingBlocks: catalog.platformCatalogAccessMatchingBlocks,
          platformCatalogAccessType: catalog.platformCatalogAccessType,
          ...values,
        },
      },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: getGetPlatformCatalogsQueryKey(currentUser?.organisation?.id ?? "") });
          void queryClient.invalidateQueries({
            queryKey: getGetPlatformCatalogByIdQueryKey(currentUser?.organisation?.id ?? "", catalogId),
          });
        },
        onError: (error) => {
          if (error.status && [400, 403].includes(error.status)) {
            setErrors(error);
          }
          if (error.status === 500) pushCreateErrorToast(t("catalogs:catalogs.catalog"));
        },
      }
    );
  };

  return (
    <FormProvider
      formState={{ isDirty, isValid, isSubmitSuccessful, ...otherFormState }}
      handleSubmit={handleSubmit}
      reset={reset}
      {...otherMethods}
    >
      <form onSubmit={(event) => void handleSubmit(onSubmit)(event)}>
        <NavigationBar
          showBreadcrumbs={true}
          actions={[
            <BVHINavigationBackButton key={0} fallbackNavigateOptions={{ to: "/app/catalogs" }} />,
            <BVHINavigationCreateEntityButton
              key={1}
              entity={t("catalogs:catalogs.catalog.version")}
              navigateTarget={{ to: "/app/catalogs/catalog/$catalogId/create", params: { catalogId } }}
            />,
          ]}
          labelOverride={(route) => {
            if (route.id === "/_main/app/catalogs/catalog/$catalogId") return catalog?.name ?? null;
            return null;
          }}
        />
        <Stack pt={2} spacing={2}>
          <CatalogUpdateCard />
          <Suspense fallback={<Card sx={{ height: 300, minHeight: 300 }} />}>
            <CatalogVersionsDataGrid requestParams={requestParams} updateCallback={handleDataGridChange} />
          </Suspense>
          <CatalogRulesCard />
        </Stack>
        <FormBlockerDialog isDirty={isDirty && !isSubmitSuccessful} />
      </form>
    </FormProvider>
  );
};
